import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, parseISO, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import EventModal from '../components/EventModal';
import TodoModal from '../components/TodoModal';
import { listGoogleEvents, initGoogleClient, getGoogleAuthState } from '../services/googleCalendar';
import { fetchIcsEvents } from '../services/appleCalendar';
import { supabase } from '../supabaseClient';

const { FiChevronLeft, FiChevronRight, FiPlus, FiDownload, FiRefreshCw, FiCheckSquare, FiClock, FiEye, FiEyeOff, FiTrash2, FiAlertCircle, FiClipboard, FiGrid, FiColumns, FiList, FiFilter, FiGift } = FiIcons;

export default function Calendar() {
  const { state, dispatch } = useFamilyContext();
  
  // Defensive destructuring with defaults
  const events = state?.events || [];
  const appointments = state?.appointments || [];
  const todos = state?.todos || [];
  const familyMembers = state?.familyMembers || [];
  const externalCalendars = state?.externalCalendars || [];
  const externalEvents = state?.externalEvents || [];
  const trashCalendar = state?.trashCalendar || [];
  
  const { isParent, isAdmin, user } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [isSyncing, setIsSyncing] = useState(false);
  const [authWarning, setAuthWarning] = useState(false);
  
  const [parties, setParties] = useState([]);

  // Local toggle states
  const [showTrash, setShowTrash] = useState(true);
  const [showHousehold, setShowHousehold] = useState(true);
  const [showParties, setShowParties] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all'); // all, event, appointment, birthday

  // Sync Logic - Initial and periodic
  useEffect(() => {
    // Only attempt sync if we have calendars
    if (externalCalendars && externalCalendars.length > 0) {
        syncExternalCalendars();
    }
    fetchParties();
    
    const intervalId = setInterval(() => {
       if (externalCalendars && externalCalendars.length > 0) {
           syncExternalCalendars();
       }
       fetchParties();
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [externalCalendars?.length, currentDate]);

  const fetchParties = async () => {
    try {
        const { data } = await supabase.from('parties').select('*');
        setParties(data || []);
    } catch (e) {
        console.error("Error loading parties for calendar:", e);
    }
  };

  const syncExternalCalendars = async () => {
    if (!externalCalendars || externalCalendars.length === 0) return;

    let config = null;
    try {
        const { data } = await supabase.from('app_settings').select('value').eq('key', 'integrations').single();
        if (data) config = data.value;
    } catch (e) {
        console.warn("Could not load integration settings", e);
    }

    setIsSyncing(true);
    setAuthWarning(false);
    
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);

    const hasGoogle = externalCalendars.some(c => c.source === 'google' && c.visible);
    let googleReady = false;
    
    if (hasGoogle && config) {
        try {
            await initGoogleClient(config.googleClientId, config.googleApiKey);
            const { isSignedIn } = getGoogleAuthState();
            googleReady = isSignedIn;
            if (!isSignedIn) {
                setAuthWarning(true); 
            }
        } catch (e) {
            console.error("Google Init in Calendar failed:", e);
        }
    }

    for (const cal of externalCalendars) {
      if (!cal.visible) continue;
      try {
        let events = [];
        if (cal.source === 'google' && config) {
          if (googleReady) {
             events = await listGoogleEvents(cal.id, start.toISOString(), end.toISOString());
          }
        } else if (cal.source === 'apple' || cal.type === 'ics') {
          events = await fetchIcsEvents(cal.url, start, end, config?.corsProxy, cal.name, cal.color);
        }
        if (events && events.length > 0) {
            dispatch({ type: 'SET_EXTERNAL_EVENTS', payload: { calendarId: cal.id, events } });
        }
      } catch (err) {
        console.warn(`Failed to sync calendar ${cal.name}:`, err);
      }
    }
    setIsSyncing(false);
  };

  const trashColors = {
    restmüll: '#374151',
    biomüll: '#16a34a',
    papier: '#2563eb',
    gelberSack: '#eab308',
    sperrmüll: '#ea580c',
    schadstoff: '#dc2626'
  };

  const getEventsForDate = (date) => {
    let dayItems = [];

    // 1. Internal Events & Appointments
    const internal = [...events, ...appointments].filter(e => {
        if (!e.date) return false;
        if (!isSameDay(new Date(e.date), date)) return false;
        if (typeFilter !== 'all' && e.type !== typeFilter) return false;
        return true;
    }).map(e => ({ ...e, displayType: e.type || 'event' }));
    dayItems = [...dayItems, ...internal];
    
    // 2. Todos & Household
    if (typeFilter === 'all') {
        const dayTodos = todos.filter(t => {
            if (!t.dueDate) return false;
            if (!isSameDay(new Date(t.dueDate), date)) return false;
            if (t.category === 'household' && !showHousehold) return false;
            return true;
        }).map(t => ({ 
            ...t, 
            displayType: t.category === 'household' ? 'household' : 'todo' 
        }));
        dayItems = [...dayItems, ...dayTodos];
    }
    
    // 3. Trash Events
    if (showTrash && trashCalendar && typeFilter === 'all') {
        const dayTrash = trashCalendar
            .filter(t => t.date && isSameDay(new Date(t.date), date))
            .map(t => ({ 
                ...t, 
                title: t.notes || (t.type.charAt(0).toUpperCase() + t.type.slice(1)), 
                displayType: 'trash',
                color: trashColors[t.type] || '#666'
            }));
        dayItems = [...dayItems, ...dayTrash];
    }
    
    // 4. Parties
    if (showParties && typeFilter === 'all') {
        const dayParties = parties
            .filter(p => p.date && isSameDay(new Date(p.date), date))
            .map(p => ({
                ...p,
                displayType: 'party',
                color: '#DB2777' 
            }));
        dayItems = [...dayItems, ...dayParties];
    }

    // 5. External Events
    if (typeFilter === 'all' && externalCalendars.length > 0) {
        const activeExternalIds = externalCalendars.filter(c => {
            if (!c.visible) return false; 
            if (c.visibility === 'everyone') return true;
            if (c.visibility === 'parents') return isParent || isAdmin;
            if (c.visibility === 'me') return c.addedBy === user?.id;
            return false;
        }).map(c => c.id);

        const dayExternal = externalEvents
        .filter(e => activeExternalIds.includes(e.calendarId) && e.start && isSameDay(new Date(e.start), date))
        .map(e => ({
            ...e,
            displayType: 'external',
            color: externalCalendars.find(c => c.id === e.calendarId)?.color || '#999'
        }));
        dayItems = [...dayItems, ...dayExternal];
    }

    return dayItems;
  };

  const navigateDate = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (viewMode === 'month') newDate.setMonth(prev.getMonth() + direction);
      else if (viewMode === 'week') newDate.setDate(prev.getDate() + (direction * 7));
      else newDate.setDate(prev.getDate() + direction);
      return newDate;
    });
  };

  const handleAddEvent = (eventData) => {
    if (eventData.type === 'event' || eventData.type === 'birthday') {
      dispatch({ type: 'ADD_EVENT', payload: eventData });
    } else {
      dispatch({ type: 'ADD_APPOINTMENT', payload: eventData });
    }
  };

  const handleSaveTask = (taskData) => {
      if (taskData.id) {
          dispatch({ type: 'UPDATE_TODO', payload: taskData });
      } else {
          dispatch({ type: 'ADD_TODO', payload: taskData });
      }
      setShowTodoModal(false);
      setSelectedTask(null);
  };

  const toggleCalendarVisibility = (id) => {
    dispatch({ type: 'TOGGLE_CALENDAR_VISIBILITY', payload: id });
  };

  const handleItemClick = (e, item) => {
      e.stopPropagation();
      if (item.displayType === 'household' || item.displayType === 'todo') {
          setSelectedTask(item);
          setShowTodoModal(true);
      } else if (item.displayType === 'party') {
          window.location.hash = '/parties'; 
      }
  };

  const renderEventPill = (item, index, detailed = false) => {
    let style = {};
    let icon = null;

    if (item.displayType === 'todo') {
      style = {
        backgroundColor: 'white',
        borderLeft: `3px solid ${item.completed ? '#10B981' : '#F59E0B'}`,
        color: '#374151',
        border: '1px solid #E5E7EB'
      };
      icon = <SafeIcon icon={FiCheckSquare} className={`w-3 h-3 mr-1 ${item.completed ? 'text-green-500' : 'text-yellow-500'}`} />;
    } else if (item.displayType === 'household') {
      style = {
        backgroundColor: '#EFF6FF',
        borderLeft: `3px solid ${item.completed ? '#10B981' : '#3B82F6'}`,
        color: '#1E40AF',
        border: '1px solid #DBEAFE'
      };
      icon = <SafeIcon icon={FiClipboard} className="w-3 h-3 mr-1 text-blue-600" />;
    } else if (item.displayType === 'appointment' || item.displayType === 'event' || item.displayType === 'birthday') {
      const member = familyMembers.find(m => m.id === item.assignedTo);
      const bgColor = member?.color || '#6B7280';
      style = { backgroundColor: bgColor, color: 'white' };
      icon = <SafeIcon icon={FiClock} className="w-3 h-3 mr-1 text-white/80" />;
      if (item.recurring && item.recurring !== 'none') {
          icon = <SafeIcon icon={FiIcons.FiRepeat} className="w-3 h-3 mr-1 text-white/80" />;
      }
    } else if (item.displayType === 'trash') {
        style = { backgroundColor: item.color, color: 'white' };
        icon = <SafeIcon icon={FiTrash2} className="w-3 h-3 mr-1 text-white/90" />;
    } else if (item.displayType === 'external') {
      style = { backgroundColor: item.color || '#9ca3af', color: 'white', opacity: 0.8 };
    } else if (item.displayType === 'party') {
      style = { backgroundColor: '#DB2777', color: 'white' };
      icon = <SafeIcon icon={FiGift} className="w-3 h-3 mr-1 text-white/90" />;
    }

    return (
      <div 
        key={`${item.displayType}-${item.id}-${index}`} 
        onClick={(e) => handleItemClick(e, item)}
        className={`flex items-center px-2 py-1 rounded text-xs truncate mb-1 shadow-sm cursor-pointer hover:opacity-80 transition-opacity ${item.displayType === 'todo' || item.displayType === 'household' ? 'border-l-4' : ''} ${detailed ? 'p-3 mb-2' : ''}`}
        style={style}
        title={item.title}
      >
        {icon}
        <span className={`truncate ${detailed ? 'text-sm font-medium' : ''}`}>{item.title}</span>
        {(item.time || detailed) && <span className="ml-auto opacity-75 text-[10px] pl-2">{item.time || (item.date && format(new Date(item.date), 'HH:mm'))}</span>}
      </div>
    );
  };

  const renderMonthView = () => {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      
      // FIXED: Correct weekday alignment starting grid from the first Monday
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

      return (
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col h-full overflow-hidden dark:bg-gray-800 dark:border-gray-700">
             <div className="grid grid-cols-7 gap-1 border-b border-gray-100 pb-2 mb-2 dark:border-gray-700">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400">{day}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1 flex-1 auto-rows-fr overflow-y-auto">
                {calendarDays.map(date => {
                    const dayItems = getEventsForDate(date);
                    const isCurrentMonth = isSameMonth(date, currentDate);
                    const isCurrentDay = isToday(date);
                    
                    return (
                        <div 
                            key={date.toISOString()}
                            onClick={() => { setSelectedDate(date); setShowEventModal(true); }}
                            className={`min-h-[80px] p-1 border border-gray-50 rounded-lg cursor-pointer transition-colors flex flex-col dark:border-gray-700 ${
                                isCurrentMonth ? 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700' : 'bg-gray-50/50 text-gray-400 dark:bg-gray-900/50 dark:text-gray-600'
                            } ${isCurrentDay ? 'ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        >
                            <div className={`text-xs font-bold mb-1 px-1 ${isCurrentDay ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                {format(date, 'd')}
                            </div>
                            <div className="space-y-1 overflow-hidden flex-1">
                                {dayItems.slice(0, 4).map((item, idx) => renderEventPill(item, idx))}
                                {dayItems.length > 4 && (
                                    <div className="text-[10px] text-gray-400 text-center font-medium">+{dayItems.length - 4}</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
      );
  };

  const renderWeekView = () => {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start, end });

      return (
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden dark:bg-gray-800 dark:border-gray-700">
              <div className="grid grid-cols-7 divide-x divide-gray-200 border-b border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                  {days.map(day => (
                      <div key={day.toString()} className={`p-3 text-center ${isToday(day) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                          <p className="text-xs text-gray-500 uppercase dark:text-gray-400">{format(day, 'EEE', { locale: de })}</p>
                          <p className={`text-lg font-bold ${isToday(day) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>{format(day, 'd')}</p>
                      </div>
                  ))}
              </div>
              <div className="flex-1 grid grid-cols-7 divide-x divide-gray-200 overflow-y-auto dark:divide-gray-700">
                   {days.map(day => {
                       const dayItems = getEventsForDate(day);
                       return (
                           <div 
                                key={day.toISOString()} 
                                className="p-2 space-y-2 min-h-[200px] hover:bg-gray-50 cursor-pointer dark:hover:bg-gray-750"
                                onClick={() => { setSelectedDate(day); setShowEventModal(true); }}
                            >
                               {dayItems.map((item, idx) => renderEventPill(item, idx, true))}
                           </div>
                       )
                   })}
              </div>
          </div>
      );
  };

  const renderDayView = () => {
      const dayItems = getEventsForDate(currentDate);
      return (
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col h-full overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{format(currentDate, 'EEEE', { locale: de })}</h3>
                    <p className="text-gray-500 dark:text-gray-400">{format(currentDate, 'd. MMMM yyyy', { locale: de })}</p>
                  </div>
                  <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg font-medium dark:bg-blue-900/30 dark:text-blue-300">
                      {dayItems.length} Einträge
                  </div>
              </div>
              <div className="space-y-3 max-w-3xl mx-auto w-full">
                  {dayItems.length === 0 && (
                      <div className="text-center py-12 text-gray-400">
                          <p>Keine Einträge für diesen Tag.</p>
                      </div>
                  )}
                  {dayItems.map((item, idx) => renderEventPill(item, idx, true))}
              </div>
          </div>
      );
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-140px)]">
      <div className="flex-1 flex flex-col space-y-4 h-full">
        {/* Toolbar */}
        <div className="flex items-center justify-between bg-white p-3 rounded-xl shadow-sm border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <div className="flex bg-gray-100 rounded-lg p-1 mr-4 dark:bg-gray-700">
                <button onClick={() => setViewMode('month')} className={`p-2 rounded-md transition-all ${viewMode === 'month' ? 'bg-white shadow text-blue-600 dark:bg-gray-600 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`} title="Monat">
                    <SafeIcon icon={FiGrid} className="w-4 h-4" />
                </button>
                <button onClick={() => setViewMode('week')} className={`p-2 rounded-md transition-all ${viewMode === 'week' ? 'bg-white shadow text-blue-600 dark:bg-gray-600 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`} title="Woche">
                    <SafeIcon icon={FiColumns} className="w-4 h-4" />
                </button>
                <button onClick={() => setViewMode('day')} className={`p-2 rounded-md transition-all ${viewMode === 'day' ? 'bg-white shadow text-blue-600 dark:bg-gray-600 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`} title="Tag">
                    <SafeIcon icon={FiList} className="w-4 h-4" />
                </button>
            </div>
            
            <button onClick={() => navigateDate(-1)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg dark:hover:bg-gray-700">
              <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-gray-900 min-w-[150px] text-center dark:text-white">
              {viewMode === 'day' ? format(currentDate, 'd. MMM yyyy', { locale: de }) : format(currentDate, 'MMMM yyyy', { locale: de })}
            </h2>
            <button onClick={() => navigateDate(1)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg dark:hover:bg-gray-700">
              <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {authWarning && (
                <a href="#/settings" title="Google Login erforderlich" className="p-2 rounded-lg text-yellow-600 bg-yellow-50 animate-pulse">
                     <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
                </a>
            )}
            <button onClick={() => { syncExternalCalendars(); fetchParties(); }} className={`p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 ${isSyncing ? 'animate-spin' : ''}`}>
               <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
            </button>
            <button 
              onClick={() => {
                setSelectedDate(new Date());
                setShowEventModal(true);
              }}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Termin</span>
            </button>
          </div>
        </div>

        {/* View Content */}
        <div className="flex-1 overflow-hidden">
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'week' && renderWeekView()}
            {viewMode === 'day' && renderDayView()}
        </div>
      </div>

      {/* Sidebar for Calendars */}
      <div className="w-full lg:w-64 bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col h-fit dark:bg-gray-800 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 mb-4 dark:text-white">Einstellungen</h3>
        
        <div className="space-y-4">
            <div>
                 <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Filter</p>
                 <select 
                    value={typeFilter} 
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm mb-3 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                 >
                     <option value="all">Alle Typen</option>
                     <option value="event">Events</option>
                     <option value="appointment">Termine</option>
                     <option value="birthday">Geburtstage</option>
                 </select>

                <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded -ml-1 dark:hover:bg-gray-750">
                    <input 
                        type="checkbox" 
                        checked={showParties} 
                        onChange={() => setShowParties(!showParties)} 
                        className="rounded text-pink-600 focus:ring-pink-500" 
                    />
                    <div className="w-3 h-3 rounded-full flex-shrink-0 bg-pink-600" />
                    <span className="text-sm text-gray-700 truncate dark:text-gray-300">Feierplaner</span>
                 </label>

                 <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded -ml-1 dark:hover:bg-gray-750">
                    <input 
                        type="checkbox" 
                        checked={showTrash} 
                        onChange={() => setShowTrash(!showTrash)} 
                        className="rounded text-blue-600 focus:ring-blue-500" 
                    />
                    <div className="w-3 h-3 rounded-full flex-shrink-0 bg-gray-700" />
                    <span className="text-sm text-gray-700 truncate dark:text-gray-300">Müllabfuhr</span>
                 </label>
                 <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded -ml-1 dark:hover:bg-gray-750">
                    <input 
                        type="checkbox" 
                        checked={showHousehold} 
                        onChange={() => setShowHousehold(!showHousehold)} 
                        className="rounded text-blue-600 focus:ring-blue-500" 
                    />
                    <div className="w-3 h-3 rounded-full flex-shrink-0 bg-blue-500" />
                    <span className="text-sm text-gray-700 truncate dark:text-gray-300">Haushalt</span>
                 </label>
            </div>

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Familie</p>
            <div className="space-y-2">
              {familyMembers.map(member => (
                <div key={member.id} className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: member.color }} />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{member.name}</span>
                </div>
              ))}
            </div>
          </div>

          {externalCalendars && externalCalendars.length > 0 && (
            <div>
               <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 mt-4">Externe Kalender</p>
               <div className="space-y-2">
                 {externalCalendars.map(cal => {
                    let isPermitted = false;
                    if (cal.visibility === 'everyone') isPermitted = true;
                    else if (cal.visibility === 'parents' && (isParent || isAdmin)) isPermitted = true;
                    else if (cal.visibility === 'me' && cal.addedBy === user?.id) isPermitted = true;

                    if (!isPermitted) return null;

                    return (
                        <label key={cal.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded -ml-1 dark:hover:bg-gray-750">
                            <input 
                                type="checkbox" 
                                checked={cal.visible} 
                                onChange={() => toggleCalendarVisibility(cal.id)} 
                                className="rounded text-blue-600 focus:ring-blue-500" 
                            />
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
                            <span className="text-sm text-gray-700 truncate dark:text-gray-300">{cal.name}</span>
                        </label>
                   );
                 })}
               </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            <a href="#/settings" className="flex items-center text-sm text-blue-600 hover:underline dark:text-blue-400">
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
              Kalender hinzufügen
            </a>
          </div>
        </div>
      </div>

      {showEventModal && (
        <EventModal 
          selectedDate={selectedDate} 
          onClose={() => setShowEventModal(false)}
          onSave={handleAddEvent} 
        />
      )}

      {showTodoModal && (
          <TodoModal
            todo={selectedTask}
            onClose={() => setShowTodoModal(false)}
            onSave={handleSaveTask}
          />
      )}
    </div>
  );
}